// File: frontend/src/components/admin/AdminTeams.tsx
// src/components/admin/AdminTeams.tsx

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaPlus,
  FaEdit,
  FaTrash,
  FaShieldAlt,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaToggleOn,
  FaToggleOff,
  FaBuilding,
  FaLock,
  FaUnlock,
  FaInfoCircle,
} from "react-icons/fa";

import { TeamModel, getTeamUrlSlug } from "../../models/TeamModel";
import { SportTypeEnum } from "../../models/enums/SportTypeEnum";
import { MatchGenderEnum } from "../../models/enums/MatchGenderEnum";
import { ClubModel } from "../../models/ClubModel";
import { getAdminClubs } from "../../services/adminClubService";
import {
  AdminTeamFilters,
  CreateAdminTeamRequest,
  UpdateAdminTeamRequest,
  getAdminTeams,
  createAdminTeam,
  updateAdminTeam,
  deleteAdminTeam,
  toggleTeamActive,
  setTeamLocked,
} from "../../services/adminTeamService";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import { ConfirmModal } from "../common/ConfirmModal";
import { LockReasonFields } from "./common/LockReasonFields";
import { LockInfoModal } from "./common/LockInfoModal";
import { SearchableSelect, SearchableSelectOption } from "./common/SearchableSelect";
import {
  ADMIN_PAGE_SIZE,
  DROPDOWN_BLUR_DELAY_MS,
  ADMIN_CLUB_SHORT_NAME_MAX_LENGTH,
  ADMIN_TEAM_CODE_MAX_LENGTH,
  ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { InputClearButton } from "../common/InputClearButton";
import { AdminCharCounter, AdminFieldInfoTooltip } from "./common";
import { MdCountryDto, getPublicCountries } from "../../services/masterDataService";
import "./AdminTeams.css";

interface AdminTeamsProps {
  isAdmin: boolean;
}

// Sport type badge color mapping
const getSportColor = (sport: string): string => {
  const map: Record<string, string> = {
    FOOTBALL:         "sport-football",
    BASKETBALL:       "sport-basketball",
    VOLLEYBALL:       "sport-volleyball",
    HANDBALL:         "sport-handball",
    ICE_HOCKEY:       "sport-icehockey",
    AMERICAN_FOOTBALL:"sport-americanfootball",
    BASEBALL:         "sport-baseball",
  };
  return map[sport] || "sport-default";
};

// Gender badge color mapping
const getGenderColor = (gender: string): string => {
  const map: Record<string, string> = {
    MALE:   "gender-male",
    FEMALE: "gender-female",
    MIXED:  "gender-mixed",
  };
  return map[gender] || "gender-default";
};

export const AdminTeams: React.FC<AdminTeamsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // Content for the (i) tooltip next to the "Display Name" field — lists every
  // place this value can end up being shown, so the admin can predict the
  // effect of a change before saving it. Shared by the create and edit modals.
  const teamDisplayNameInfoItems = [
    t("admin_team_display_name_info_item_1"),
    t("admin_team_display_name_info_item_2"),
    t("admin_team_display_name_info_item_3"),
    t("admin_team_display_name_info_item_4"),
    t("admin_team_display_name_info_item_5"),
    t("admin_team_display_name_info_item_6"),
    t("admin_team_display_name_info_item_7"),
  ];

  // ── Data state ────────────────────────────────────────────────
  const [teams, setTeams] = useState<TeamModel[]>([]);

  // ── Lock modal state (shown only when locking, not unlocking) ──
  const [lockTarget, setLockTarget] = useState<TeamModel | null>(null);
  const [lockReasonDraft, setLockReasonDraft] = useState("");
  const [lockReasonVisibleDraft, setLockReasonVisibleDraft] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockInfoTarget, setLockInfoTarget] = useState<TeamModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // ── Pagination state ──────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // ── Filter state ──────────────────────────────────────────────
  const [filters, setFilters] = useState<AdminTeamFilters>({
    search:    "",
    sportType: "",
    gender:    "",
    active:    "",
    clubId:    "",
    country:   "",
    locked:    "",
    sortBy:    "clubShortName",
    sortDir:   "asc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // ── Modal state ───────────────────────────────────────────────
  const [modalType, setModalType] = useState<"create" | "edit" | "delete" | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamModel | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // ── Form state ────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    clubId:      0,
    sportType:   SportTypeEnum.FOOTBALL as string,
    gender:      MatchGenderEnum.MALE as string,
    // Sub-team code: blank = main team, "a", "1b", "u19", etc.
    teamCode:    "",
    displayName: "",
    active:      true,
  });

  // ── Club search for create modal (2-step: search club → pick sport/gender) ──
  const [allClubs, setAllClubs] = useState<ClubModel[]>([]);
  // Country list used by the searchable country filter
  const [countries, setCountries] = useState<MdCountryDto[]>([]);

  useEffect(() => {
    getPublicCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  const [clubSearch, setClubSearch] = useState("");
  const [selectedClub, setSelectedClub] = useState<ClubModel | null>(null);
  const [showClubDropdown, setShowClubDropdown] = useState(false);


  // Load ALL clubs directly from admin endpoint (active only for dropdown).
  // Using the admin API ensures clubs with no teams yet (e.g. newly created clubs) also appear.
  useEffect(() => {
    const loadClubs = async () => {
      try {
        const response = await getAdminClubs(0, 500, {
          search: "", country: "", active: "true",
          sortBy: "shortName", sortDir: "asc",
        });
        setAllClubs(response.content as unknown as ClubModel[]);
      } catch (err) {
        console.error("Failed to load clubs for dropdown:", err);
      }
    };
    loadClubs();
  }, []);


  // Filtered club dropdown results
  const filteredClubs = React.useMemo(() => {
    if (!clubSearch || selectedClub) return [];
    const q = clubSearch.toLowerCase();
    return allClubs
      .filter(
        (c) =>
          c.shortName.toLowerCase().includes(q) ||
          (c.longName && c.longName.toLowerCase().includes(q)) ||
          (c.abbreviation && c.abbreviation.toLowerCase().includes(q))
      )
      .slice(0, 10);
  }, [clubSearch, allClubs, selectedClub]);

  // Options for the searchable club filter — empty value acts as the "all" entry
  const clubFilterOptions: SearchableSelectOption[] = React.useMemo(() => [
    { value: "", label: t("admin_all") },
    ...allClubs.map((c) => ({
      value: String(c.id),
      label: c.shortName,
      hint: c.country ?? undefined,
    })),
  ], [allClubs, t]);

  // Options for the searchable country filter — empty value acts as the "all" entry
  const countryFilterOptions: SearchableSelectOption[] = React.useMemo(() => [
    { value: "", label: t("admin_all_countries") },
    ...countries.map((c) => ({ value: c.code, label: `${c.code} — ${c.displayName}` })),
  ], [countries, t]);

  // ── Fetch teams ───────────────────────────────────────────────
  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminTeams(currentPage, ADMIN_PAGE_SIZE, filters);
      setTeams(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err: any) {
      setError(err.message || t("admin_team_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, t]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // ── Toolbar handlers ──────────────────────────────────────────
  const handleSearch = () => {
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, search: searchInput }));
  };

  // Clear-X next to the search box: empties the field AND drops the applied search
  const handleClearSearch = () => {
    setSearchInput("");
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy:  field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(0);
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc"
      ? <FaSortUp   className="sort-icon active" />
      : <FaSortDown className="sort-icon active" />;
  };

  const handleClearFilters = () => {
    setFilters({ search: "", sportType: "", gender: "", active: "", clubId: "", country: "", locked: "", sortBy: "clubShortName", sortDir: "asc" });
    setSearchInput("");
    setCurrentPage(0);
  };

  const hasActiveFilters =
    !!filters.search || !!filters.sportType || !!filters.gender ||
    filters.active !== "" || filters.clubId !== "" || !!filters.country || filters.locked !== "";

  const handlePaginate = (page: number) => setCurrentPage(page - 1);

  // ── Modal helpers ─────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ clubId: 0, sportType: SportTypeEnum.FOOTBALL, gender: MatchGenderEnum.MALE, teamCode: "", displayName: "", active: true });
    setClubSearch("");
    setSelectedClub(null);
    setModalError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalType("create");
  };

  const openEditModal = (team: TeamModel) => {
    setSelectedTeam(team);
    setFormData({
      clubId:      team.clubId,
      sportType:   team.sportType,
      gender:      team.gender,
      // teamCode is immutable after creation — populated here for display only
      teamCode:    team.teamCode ?? "",
      displayName: team.displayName ?? "",
      active:      team.active,
    });
    setModalError(null);
    setModalType("edit");
  };

  // ── Topic lock/unlock ──────────────────────────────────────────────────────
  const handleToggleLock = async (team: TeamModel) => {
    if (!team.isLocked) {
      // About to lock — collect an optional reason first
      setLockTarget(team);
      setLockReasonDraft("");
      setLockReasonVisibleDraft(false);
      return;
    }
    try {
      const updated = await setTeamLocked(team.id, false);
      setTeams(prev => prev.map(x => x.id === team.id ? updated : x));
      setSuccessMessage(t("admin_topic_unlocked"));
    } catch {
      setError(t("admin_action_failed"));
    }
  };

  const handleConfirmLock = async () => {
    if (!lockTarget) return;
    setLockBusy(true);
    try {
      const updated = await setTeamLocked(lockTarget.id, true, lockReasonDraft.trim() || undefined, lockReasonVisibleDraft);
      setTeams(prev => prev.map(x => x.id === lockTarget.id ? updated : x));
      setSuccessMessage(t("admin_topic_locked"));
      setLockTarget(null);
    } catch {
      setError(t("admin_action_failed"));
    } finally {
      setLockBusy(false);
    }
  };

  const handleCancelLock = () => {
    setLockTarget(null);
    setLockReasonDraft("");
    setLockReasonVisibleDraft(false);
  };

  const openDeleteModal = (team: TeamModel) => {
    setSelectedTeam(team);
    setModalError(null);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedTeam(null);
    setModalError(null);
  };

  // ── Club selection for create modal ──────────────────────────
  const selectClub = (club: ClubModel) => {
    setSelectedClub(club);
    setClubSearch(club.shortName);
    setShowClubDropdown(false);
    setFormData((p) => ({ ...p, clubId: club.id }));
  };

  const clearClub = () => {
    setSelectedClub(null);
    setClubSearch("");
    setFormData((p) => ({ ...p, clubId: 0 }));
  };

  // ── CRUD handlers ─────────────────────────────────────────────
  const handleCreate = async () => {
    if (!formData.clubId) {
      setModalError(t("admin_team_club_required"));
      return;
    }
    try {
      const req: CreateAdminTeamRequest = {
        clubId:      formData.clubId,
        sportType:   formData.sportType,
        gender:      formData.gender,
        teamCode:    formData.teamCode.trim() || undefined,
        displayName: formData.displayName.trim() || undefined,
      };
      await createAdminTeam(req);
      setSuccessMessage(t("admin_team_created"));
      closeModal();
      fetchTeams();
    } catch (err: any) {
      setModalError(err.message || t("admin_team_create_error"));
    }
  };

  const handleUpdate = async () => {
    if (!selectedTeam) return;
    try {
      const req: UpdateAdminTeamRequest = {
        displayName: formData.displayName.trim() || null,
        active:      formData.active,
      };
      await updateAdminTeam(selectedTeam.id, req);
      setSuccessMessage(t("admin_team_updated"));
      closeModal();
      fetchTeams();
    } catch (err: any) {
      setModalError(err.message || t("admin_team_update_error"));
    }
  };

  const handleDelete = async () => {
    if (!selectedTeam) return;
    try {
      await deleteAdminTeam(selectedTeam.id);
      setSuccessMessage(t("admin_team_deleted"));
      closeModal();
      fetchTeams();
    } catch (err: any) {
      setModalError(err.message || t("admin_team_delete_error"));
    }
  };

  // Quick-toggle active from table row
  const handleToggleActive = async (team: TeamModel) => {
    try {
      await toggleTeamActive(team.id, !team.active);
      const label = team.displayName ?? team.clubShortName;
      setSuccessMessage(!team.active ? t("admin_toggle_activated", { name: label }) : t("admin_toggle_deactivated", { name: label }));
      fetchTeams();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="admin-teams">

      <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_team_search_placeholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
          </button>
        </div>

        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {t("admin_filters")}
          <FaChevronDown className={`chevron ${showFilters ? "open" : ""}`} />
        </button>

        {hasActiveFilters && (
          <button className="clear-filters" onClick={handleClearFilters}>
            <FaTimes /> {t("admin_clear_filters")}
          </button>
        )}

        <button className="create-team-btn" onClick={openCreateModal}>
          <FaPlus /> {t("admin_team_add")}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_sport_type_label")}</label>
            <select
              value={filters.sportType}
              onChange={(e) => { setFilters((p) => ({ ...p, sportType: e.target.value })); setCurrentPage(0); }}
            >
              <option value="">{t("admin_all_sports")}</option>
              {Object.values(SportTypeEnum).map((s) => (
                <option key={s} value={s}>{t(`sport_type_${s.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>

          {/* Club filter — typeable dropdown over the full club list */}
          <div className="admin-filter-group">
            <label>{t("admin_team_col_club")}</label>
            <SearchableSelect
              value={filters.clubId === "" ? "" : String(filters.clubId)}
              options={clubFilterOptions}
              placeholder={t("admin_all")}
              onChange={(v) => {
                setFilters((p) => ({ ...p, clubId: v === "" ? "" : Number(v) }));
                setCurrentPage(0);
              }}
            />
          </div>

          {/* Country filter — typeable dropdown over the club country list */}
          <div className="admin-filter-group">
            <label>{t("admin_country")}</label>
            <SearchableSelect
              value={filters.country}
              options={countryFilterOptions}
              placeholder={t("admin_all_countries")}
              onChange={(v) => { setFilters((p) => ({ ...p, country: v })); setCurrentPage(0); }}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_gender_label")}</label>
            <select
              value={filters.gender}
              onChange={(e) => { setFilters((p) => ({ ...p, gender: e.target.value })); setCurrentPage(0); }}
            >
              <option value="">{t("admin_all")}</option>
              {Object.values(MatchGenderEnum).map((g) => (
                <option key={g} value={g}>{t(`match_gender_${g.toLowerCase()}`)}</option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_status_label")}</label>
            <select
              value={filters.active}
              onChange={(e) => { setFilters((p) => ({ ...p, active: e.target.value as AdminTeamFilters["active"] })); setCurrentPage(0); }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_active")}</option>
              <option value="false">{t("admin_inactive")}</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_ct_lock_status")}</label>
            <select
              value={filters.locked}
              onChange={(e) => { setFilters((p) => ({ ...p, locked: e.target.value as AdminTeamFilters["locked"] })); setCurrentPage(0); }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_ct_locked")}</option>
              <option value="false">{t("admin_ct_unlocked")}</option>
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="teams-table-container">
        {loading ? (
          <div className="teams-loading">{t("loading")}</div>
        ) : error && teams.length === 0 ? (
          <AdminErrorBanner message={error} />
        ) : teams.length === 0 ? (
          <div className="teams-empty">{t("admin_team_not_found")}</div>
        ) : (
          <table className="teams-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")}>
                  ID {getSortIcon("id")}
                </th>
                <th onClick={() => handleSort("logo")} className="col-logo">
                  Logo {getSortIcon("logo")}
                </th>
                <th onClick={() => handleSort("clubShortName")}>
                  {t("admin_team_col_club")} {getSortIcon("clubShortName")}
                </th>
                <th onClick={() => handleSort("displayName")} className="col-displayname">
                  {t("admin_team_col_display_name")} {getSortIcon("displayName")}
                </th>
                <th onClick={() => handleSort("sportType")} className="col-sport">
                  {t("admin_team_col_sport")} {getSortIcon("sportType")}
                </th>
                <th onClick={() => handleSort("gender")} className="col-gender">
                  {t("admin_team_col_gender")} {getSortIcon("gender")}
                </th>
                <th onClick={() => handleSort("country")} className="col-country">
                  {t("admin_country")} {getSortIcon("country")}
                </th>
                <th onClick={() => handleSort("active")} className="col-active">
                  {t("admin_status_label")} {getSortIcon("active")}
                </th>
                <th>{t("admin_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id} className={!team.active ? "row-inactive" : ""}>
                  <td className="team-id">#{team.id}</td>

                  {/* Dedicated logo column — clickable to open lightbox */}
                  <td className="col-logo">
                    {team.clubLogoUrl ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${team.clubLogoUrl}`}
                        alt={team.clubShortName}
                        className="club-logo-thumb"
                        style={{ cursor: "zoom-in" }}
                        onClick={() => setLightboxUrl(`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${team.clubLogoUrl}`)}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="club-logo-placeholder"><FaBuilding /></div>
                    )}
                  </td>

                  {/* Club cell — short name links to /takim/{clubSlug}/{teamSlug} */}
                  <td className="team-club-cell">
                    <div className="club-name-cell">
                      <Link
                        to={`/takim/${team.clubSlug}/${getTeamUrlSlug(team)}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                      >
                        <span className="club-name-text">{team.clubShortName}</span>
                        {team.clubAbbreviation && (
                          <span className="club-abbr-small">{team.clubAbbreviation}</span>
                        )}
                      </Link>
                    </div>
                  </td>

                  {/* Custom display name (e.g. "Fenerbahçe Beko") — and sub-team code if set */}
                  <td className="col-displayname">
                    {team.displayName
                      ? <span className="team-displayname">{team.displayName}</span>
                      : <span className="text-muted-cell">—</span>
                    }
                    {team.teamCode && (
                      <>
                        {" "}
                        <span className="team-code-badge">{team.teamCode.toUpperCase()}</span>
                      </>
                    )}
                  </td>

                  {/* Sport type badge */}
                  <td className="col-sport">
                    <span className={`sport-badge ${getSportColor(team.sportType)}`}>
                      {t(`sport_type_${team.sportType.toLowerCase()}`)}
                    </span>
                  </td>

                  {/* Gender badge */}
                  <td className="col-gender">
                    <span className={`gender-badge ${getGenderColor(team.gender)}`}>
                      {t(`match_gender_${team.gender.toLowerCase()}`)}
                    </span>
                  </td>

                  <td className="col-country">
                    <span className="text-muted-cell">{team.clubCountry}</span>
                  </td>

                  {/* Quick-toggle active */}
                  <td className="col-active">
                    <button
                      className={`active-toggle-btn ${team.active ? "is-active" : "is-inactive"}`}
                      title={team.active ? t("admin_toggle_active_title") : t("admin_toggle_inactive_title")}
                      onClick={() => handleToggleActive(team)}
                    >
                      {team.active
                        ? <><FaToggleOn /> {t("admin_active")}</>
                        : <><FaToggleOff /> {t("admin_inactive")}</>
                      }
                    </button>
                  </td>

                  <td className="team-actions">
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        title={t("admin_edit_btn")}
                        onClick={() => openEditModal(team)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className={team.isLocked ? "action-btn lock-active" : "action-btn lock"}
                        title={team.isLocked ? t("admin_topic_unlock") : t("admin_topic_lock")}
                        onClick={() => handleToggleLock(team)}
                      >
                        {team.isLocked ? <FaUnlock size={12} /> : <FaLock size={12} />}
                      </button>
                      {team.isLocked && (
                        <button
                          className="action-btn"
                          title={t("admin_lock_info_title")}
                          onClick={() => setLockInfoTarget(team)}
                        >
                          <FaInfoCircle size={12} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          className="action-btn danger"
                          title={t("admin_delete_btn")}
                          onClick={() => openDeleteModal(team)}
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="teams-pagination">
          <span className="page-info">
            {t("admin_team_page_info", { current: currentPage + 1, total: totalPages, items: totalItems })}
          </span>
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            paginate={handlePaginate}
          />
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Create Modal */}
      {modalType === "create" && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal team-form-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_team_create_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              {/* Step 1: Club search */}
              <div className="form-row">
                <div className="form-group team-select-group">
                  <label>{t("admin_team_col_club")} *</label>
                  <div className="team-search-wrapper">
                    <div className="club-search-row">
                      <input
                        type="text"
                        placeholder={t("admin_team_club_search_placeholder")}
                        value={clubSearch}
                        onChange={(e) => { setClubSearch(e.target.value); setSelectedClub(null); setShowClubDropdown(true); }}
                        onFocus={() => setShowClubDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClubDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                        className={selectedClub ? "club-selected" : ""}
                        maxLength={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH}
                      />
                      {selectedClub && (
                        <button type="button" className="club-clear-btn" onClick={clearClub} title={t("admin_change")}>✕</button>
                      )}
                    </div>
                    {showClubDropdown && filteredClubs.length > 0 && (
                      <div className="team-dropdown">
                        {filteredClubs.map((club) => (
                          <div key={club.id} className="team-dropdown-item" onClick={() => selectClub(club)}>
                            <span className="team-dropdown-name">{club.shortName}</span>
                            <span className="team-dropdown-country">{club.country}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedClub && (
                      <div className="team-auto-selected">
                        ✓ {selectedClub.shortName}
                        {selectedClub.abbreviation && (
                          <span className="team-auto-badge">{selectedClub.abbreviation}</span>
                        )}
                      </div>
                    )}
                    <AdminCharCounter value={clubSearch} max={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH} />
                  </div>
                </div>
              </div>

              {/* Step 2: Sport type + gender */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_sport_type_label")} *</label>
                  <select
                    value={formData.sportType}
                    onChange={(e) => setFormData((p) => ({ ...p, sportType: e.target.value }))}
                  >
                    {Object.values(SportTypeEnum).map((s) => (
                      <option key={s} value={s}>{t(`sport_type_${s.toLowerCase()}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_gender_label")} *</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData((p) => ({ ...p, gender: e.target.value }))}
                  >
                    {Object.values(MatchGenderEnum).map((g) => (
                      <option key={g} value={g}>{t(`match_gender_${g.toLowerCase()}`)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Optional team code (sub-team: 1b, u19, paf, etc.) */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_team_code_label")} <span className="label-optional">{t("admin_optional")}</span></label>
                  <input
                    type="text"
                    placeholder={t("admin_team_code_placeholder")}
                    value={formData.teamCode}
                    onChange={(e) => setFormData((p) => ({ ...p, teamCode: e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, "") }))}
                    maxLength={ADMIN_TEAM_CODE_MAX_LENGTH}
                  />
                  <AdminCharCounter value={formData.teamCode} max={ADMIN_TEAM_CODE_MAX_LENGTH} />
                </div>
              </div>

              {/* Optional display name */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    {t("admin_team_display_name_label")} <span className="label-optional">{t("admin_optional")}</span>
                    <AdminFieldInfoTooltip title={t("admin_team_display_name_info_title")} items={teamDisplayNameInfoItems} />
                  </label>
                  <input
                    type="text"
                    placeholder={t("admin_team_display_name_placeholder")}
                    value={formData.displayName}
                    maxLength={ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH}
                    onChange={(e) => setFormData((p) => ({ ...p, displayName: e.target.value }))}
                  />
                  <AdminCharCounter value={formData.displayName} max={ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH} />
                </div>
              </div>

              <div className="team-form-hint">
                <FaShieldAlt className="hint-icon" />
                {t("admin_team_unique_hint")}
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-confirm-btn" onClick={handleCreate}>{t("admin_create_btn")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal — only displayName and active are mutable */}
      {modalType === "edit" && selectedTeam && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal team-form-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_team_edit_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              {/* Read-only info block */}
              <div className="team-edit-info">
                <div className="team-edit-info-row">
                  {selectedTeam.clubLogoUrl && (
                    <img src={selectedTeam.clubLogoUrl} alt="" className="club-logo-thumb" />
                  )}
                  <strong>{selectedTeam.clubShortName}</strong>
                </div>
                <div className="team-edit-badges">
                  <span className={`sport-badge ${getSportColor(selectedTeam.sportType)}`}>
                    {t(`sport_type_${selectedTeam.sportType.toLowerCase()}`)}
                  </span>
                  <span className={`gender-badge ${getGenderColor(selectedTeam.gender)}`}>
                    {t(`match_gender_${selectedTeam.gender.toLowerCase()}`)}
                  </span>
                  <span className="team-immutable-note">{t("admin_team_immutable_note")}</span>
                </div>
              </div>

              {/* Editable: display name */}
              <div className="form-row">
                <div className="form-group">
                  <label>
                    {t("admin_team_display_name_label")} <span className="label-optional">{t("admin_optional")}</span>
                    <AdminFieldInfoTooltip title={t("admin_team_display_name_info_title")} items={teamDisplayNameInfoItems} />
                  </label>
                  <input
                    type="text"
                    placeholder={t("admin_team_display_name_placeholder")}
                    value={formData.displayName}
                    maxLength={ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH}
                    onChange={(e) => setFormData((p) => ({ ...p, displayName: e.target.value }))}
                  />
                  <AdminCharCounter value={formData.displayName} max={ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH} />
                </div>
              </div>

              {/* Editable: active toggle */}
              <div className="form-row">
                <div className="form-group form-group--inline">
                  <label>{t("admin_status_label")}</label>
                  <button
                    type="button"
                    className={`active-toggle-btn ${formData.active ? "is-active" : "is-inactive"}`}
                    onClick={() => setFormData((p) => ({ ...p, active: !p.active }))}
                  >
                    {formData.active ? <><FaToggleOn /> {t("admin_active")}</> : <><FaToggleOff /> {t("admin_inactive")}</>}
                  </button>
                </div>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-confirm-btn" onClick={handleUpdate}>{t("admin_save")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {modalType === "delete" && selectedTeam && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal delete-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_team_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <div className="delete-club-info">
                <FaShieldAlt size={32} />
                <p>
                  <strong>
                    {selectedTeam.displayName ?? selectedTeam.clubShortName}
                  </strong>
                </p>
                <div className="team-edit-badges" style={{ justifyContent: "center" }}>
                  <span className={`sport-badge ${getSportColor(selectedTeam.sportType)}`}>
                    {t(`sport_type_${selectedTeam.sportType.toLowerCase()}`)}
                  </span>
                  <span className={`gender-badge ${getGenderColor(selectedTeam.gender)}`}>
                    {t(`match_gender_${selectedTeam.gender.toLowerCase()}`)}
                  </span>
                </div>
                <p className="delete-warning-text">
                  {t("admin_confirm_irreversible")}
                </p>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-delete-btn" onClick={handleDelete}>{t("admin_delete_btn")}</button>
            </div>
          </div>
        </div>
      )}
      {/* Lightbox — full size logo preview */}
      {lightboxUrl && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxUrl}
            alt="Logo"
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }}
          />
        </div>
      )}

      {/* Lock-with-reason modal — only shown when locking, unlock stays one-click */}
      {lockTarget && (
        <ConfirmModal
          message={t("admin_team_lock_confirm")}
          confirmLabel={lockBusy ? "..." : t("admin_topic_lock")}
          cancelLabel={t("admin_cancel")}
          onConfirm={handleConfirmLock}
          onCancel={handleCancelLock}
          disabled={lockBusy}
        >
          <LockReasonFields
            reason={lockReasonDraft}
            onReasonChange={setLockReasonDraft}
            visible={lockReasonVisibleDraft}
            onVisibleChange={setLockReasonVisibleDraft}
          />
        </ConfirmModal>
      )}

      {lockInfoTarget && (
        <LockInfoModal
          lockedByUsername={lockInfoTarget.lockedByUsername}
          lockedAt={lockInfoTarget.lockedAt}
          lockReason={lockInfoTarget.lockReason}
          lockReasonVisible={lockInfoTarget.lockReasonVisible}
          onClose={() => setLockInfoTarget(null)}
        />
      )}
    </div>
  );
};